import { useState, useEffect } from "react";
import { TextField, InputAdornment, Button, IconButton } from "@mui/material";
import { MdSearch } from "react-icons/md";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import { getImportedData } from "../../../Api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function PreviewFileTable({ selectedFile, fileType, selectedTab, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [allData, setAllData] = useState([]); // Store all data for frontend pagination
  const [totalItems, setTotalItems] = useState(0);
  const [dataFetched, setDataFetched] = useState(false); // Track if data was successfully fetched

  // For sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // Add getSortableFields function
  const getSortableFields = () => {
    const selectedFileType = selectedFile?.file_type || selectedFile?.fileType;

    if (selectedFileType === "OFAC_SDN" || selectedFileType === "OFAC") {
      return ["ENT_NUM", "SDN_NAME", "SDN_TYPE"];
    } else if (
      selectedFileType === "UK_Sanctions" ||
      selectedFileType === "UKSanctions"
    ) {
      return ["UNIQUE_ID", "NAME", "TYPE"];
    } else if (
      selectedFileType === "OFAC_NON_SDN" ||
      selectedFileType === "NONSDN"
    ) {
      return ["ENT_NUM", "SDN_NAME", "SDN_TYPE"];
    } else if (selectedFileType === "PEP") {
      return ["NAME"];
    } else if (
      selectedFileType === "WATCH_LIST" ||
      selectedFileType === "Watch List"
    ) {
      return ["NAME"];
    } else if (
      selectedFileType === "Others" ||
      selectedFileType === "Miscellaneous" ||
      selectedFileType === "Negative"
    ) {
      return ["NAME", "TYPE"];
    }
    return [];
  };

  // Add handleSort function (after getFilteredData, around line 126)
  const handleSort = (columnKey) => {
    const sortableFields = getSortableFields();
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");

    if (!sortableFields.includes(normalizedKey)) {
      return;
    }

    setSortConfig((prevConfig) => {
      if (prevConfig.key === normalizedKey) {
        return {
          key: normalizedKey,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          key: normalizedKey,
          direction: "asc",
        };
      }
    });
    setCurrentPage(1);
  };

  const navigate = useNavigate();
  // Fetch data when selectedFile changes

  const tabsData = {
    0: "OFAC",
    1: "NONSDN",
    2: "UK Sanctions",
    3: "PEP",
    4: "Watch List",
    5: "Negative",
    // 6: "Address",
    OFAC: "OFAC",
    NONSDN: "NONSDN",
    "UK Sanctions": "UK_Sanctions",
    PEP: "PEP",
    "Watch List": "Watch List",
    Negative: "Negative",
    // Address: "Address",
  };

  useEffect(() => {
    const fetchData = async () => {
      // If we don't have a selected file with file_type and file_name, don't fetch
      if (!selectedFile?.file_type || !selectedFile?.file_name) return;

      const effectiveFileType = selectedFile.file_type;
      const validFileTypes = [
        "OFAC_SDN",
        "OFAC_NON_SDN",
        // "OFAC_ADD_SDN",
        // "OFAC_ADD_NON_SDN",
        "UK_Sanctions",
        "PEP",
        "WATCH_LIST",
        "Others",
      ];

      if (!effectiveFileType || !validFileTypes.includes(effectiveFileType)) {
        setError("Invalid file type");
        return;
      }

      setLoading(true);
      try {
        Swal.fire({
          html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });

        // Fetch all data at once for frontend pagination
        const response = await getImportedData({
          file_type: effectiveFileType,
          file_name: selectedFile?.file_name || null, // Use null if no file name is provided
          bank_code: localStorage.getItem("bank_code") || "",
          page: 1,
          pageSize: 10000, // Large page size to get all data
          searchTerm: "", // No search term for initial fetch
        });

        Swal.close();

        if (response?.status === "S") {
          // Store all data for frontend pagination
          setAllData(response.result_info || []);
          setTotalItems(response.result_info ? response.result_info.length : 0);
          setCurrentPage(1); // Reset to first page
          setDataFetched(true); // Mark data as successfully fetched
        } else if (
          response?.status === "F" &&
          response?.message === "Unauthorized - Missing token"
        ) {
          Swal.fire({
            text: "Please login with your credentials",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            localStorage.clear();
            navigate("/login");
          });
        } else {
          setError(response?.message || "Failed to fetch data");
        }
      } catch (err) {
        setError(err?.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFile, selectedTab, navigate]);

  useEffect(() => {
    // Scroll to table top when page changes
    const section = document.getElementById("previewSection");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPage]);

  // Add this function to filter and paginate table data
  const getFilteredTableData = () => {
    if (!allData || allData.length === 0) return [];

    let filteredData = allData;
    if (searchTerm.trim()) {
      const searchFields = {
        OFAC: ["ent_num", "sdn_name", "sdn_type"],
        NONSDN: ["ent_num", "sdn_name", "sdn_type"],
        UKSanctions: ["unique_id", "name", "type"],
        PEP: ["name"], // partial
        "Watch List": ["name"], // partial
        Negative: ["file_type"], // exact
      };

      // Determine current tab name consistently
      const tabMap = {
        OFAC_SDN: "OFAC",
        OFAC_NON_SDN: "NONSDN",
        UK_Sanctions: "UKSanctions",
        WATCH_LIST: "Watch List",
        PEP: "PEP",
        Others: "Negative",
        Negative: "Negative",
      };

      const effectiveTab =
        tabMap[selectedFile?.file_type] || tabMap[fileType] || "OFAC";

      const currentTabFields = searchFields[effectiveTab];

      filteredData = allData.filter((row) => {
        return Object.entries(row).some(([key, value]) => {
          if (!value) return false;

          const lowerKey = key.toLowerCase();
          const lowerValue = value.toString().toLowerCase();
          const lowerSearch = searchTerm.toLowerCase();

          // Normalize spaces
          const normalizedValue = lowerValue.trim().replace(/\s+/g, " ");
          const normalizedSearch = lowerSearch.trim().replace(/\s+/g, " ");

          // Only search within allowed fields
          if (currentTabFields && currentTabFields.includes(lowerKey)) {
            switch (effectiveTab) {
              //  OFAC & NONSDN — exact for ent_num/sdn_type, partial for sdn_name
              case "OFAC":
              case "NONSDN":
                if (lowerKey === "sdn_name") {
                  return normalizedValue.includes(normalizedSearch);
                } else {
                  return normalizedValue === normalizedSearch;
                }

              // UKSanctions — exact for unique_id/type, partial for name
              case "UKSanctions":
                if (lowerKey === "name") {
                  return normalizedValue.includes(normalizedSearch);
                } else {
                  return normalizedValue === normalizedSearch;
                }

              // PEP & Watch List — partial (name only)
              case "PEP":
              case "Watch List":
                return normalizedValue.includes(normalizedSearch);

              // Negative — exact (file_type)
              case "Negative":
                return normalizedValue === normalizedSearch;

              default:
                return normalizedValue.includes(normalizedSearch);
            }
          }

          // Fallback: partial search across all fields
          if (!currentTabFields) {
            return normalizedValue.includes(normalizedSearch);
          }

          return false;
        });
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredData = [...filteredData].sort((a, b) => {
        const getValue = (row, key) => {
                 // Special handling for TYPE column (Negative case uses file_type)
          if (key === "TYPE") {
            return (
              row["type"] || 
              row["file_type"] || 
              row["FILE_TYPE"] || 
              ""
            );
          }
          // For other keys, try multiple formats
          return (
            row[key.toLowerCase()] || 
            row[key] || 
            row[key.toUpperCase()] || 
            ""
          );
        };

        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);

        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        const isNumeric =
          !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

        let comparison = 0;
        if (isNumeric) {
          comparison = aNum - bNum;
        } else {
          const aStr = String(aValue || "").toLowerCase();
          const bStr = String(bValue || "").toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }

        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    // Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Add function to render sort icon (add this helper function)
  const renderSortIcon = (columnKey) => {
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");
    const sortableFields = getSortableFields();

    if (!sortableFields.includes(normalizedKey)) {
      return null;
    }

    const isActive = sortConfig.key === normalizedKey;
    const isAsc = sortConfig.direction === "asc";

    if (!isActive) return <FaSort className="sort-icon inactive" />;

    return isAsc ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  const getTableHeaders = () => {
    const selectedFileType = selectedFile?.file_type || selectedFile?.fileType;
    // Special handling for Address tab files
    if (
      selectedFileType === "OFAC_ADD_SDN" ||
      selectedFileType === "OFAC_ADD_NON_SDN"
    ) {
      // Use consistent keys so renderer picks up date/time formatting
      return [
        "#",
        "ID",
        "ID1",
        "ADDRESS1",
        "ADDRESS2",
        "ADDRESS3",
        "ADDRESS4",
        "LOAD_DATE",
        "LOAD_TIME",
      ];
    }
    // Handle other file types
    switch (selectedFileType) {
      case "OFAC_SDN":
      case "OFAC":
        return [
          "#",
          "ENT_NUM",
          "SDN_NAME",
          "SDN_TYPE",
          "PROGRAM",
          "TITLE",
          "CALL_SIGN",
          "VESSEL_TYPE",
          "TONNAGE",
          "GRT",
          "VESSEL_FLAG",
          "VESSEL_OWNER",
          "REMARKS",
        ];
      case "UK_Sanctions":
      case "UKSanctions":
        return [
          "#",
          "UNIQUE_ID",
          "OFSI_GROUP_ID",
          "NAME",
          "REGIME_NAME",
          "TYPE",
          "DESIGNATION_SOURCE",
          "DATE_DESIGNATED",
          "SANCTIONS_IMPOSED",
        ];
      case "OFAC_NON_SDN":
      case "NONSDN":
        return [
          "#",
          "ENT_NUM",
          "SDN_NAME",
          "SDN_TYPE",
          "PROGRAM",
          "TITLE",
          "CALL_SIGN",
          "VESSEL_TYPE",
          "TONNAGE",
          "GRT",
          "VESSEL_FLAG",
          "VESSEL_OWNER",
          "REMARKS",
        ];
      case "Others":
      case "PEP":
        return [
          "#",
          "UNIQUE_ID",
          "NAME",
          "TYPE",
          "LOAD_DATE",
          "LOAD_TIME",
        ];
      case "Others":
      case "WATCH_LIST":
        return [
          "#",
          "UNIQUE_ID",
          "NAME",
          "TYPE",
          "LOAD_DATE",
          "LOAD_TIME",
        ];
      case "Others":
      case "Miscellaneous":
      case "Negative":
        return [
          "#",
          "UNIQUE_ID",
          "NAME",
          "TYPE",
          "LOAD_DATE",
          "LOAD_TIME",
        ];
      case "Address":
        return [
          "#",
          "ID",
          "ID1",
          "ADDRESS1",
          "ADDRESS2",
          "ADDRESS3",
          "ADDRESS4",
          "LOAD_DATE",
          "LOAD_TIME",
        ];

      default:
        return ["#", "ID", "File Name", "Created Date", "Load Time"];
    }
  };

  // Handle search input
  const handleSearch = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    // Reset to first page when searching
    if (newSearchTerm !== searchTerm) {
      setCurrentPage(1);
    }
  };

  // Pagination handlers
  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () =>
    setCurrentPage(Math.ceil(totalItems / itemsPerPage));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) =>
      Math.min(prev + 1, Math.ceil(totalItems / itemsPerPage))
    );

  useEffect(() => {
    setSearchTerm("");
    setCurrentPage(1);
    setDataFetched(false); // Reset data fetched status when file or tab changes
    setSortConfig({ key: null, direction: "asc" }); // Reset sorting when file or tab changes
  }, [selectedFile, selectedTab]);

  // Add a useEffect to update total items count when search or data changes
  useEffect(() => {
    if (!allData || allData.length === 0) {
      setTotalItems(0);
      return;
    }

    // Filter the data based on search term
    let filteredData = allData;
    if (searchTerm.trim()) {
      filteredData = allData.filter((row) =>
        Object.values(row).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setTotalItems(filteredData.length);
  }, [allData, searchTerm]);

  return (
    <div className="mt-4" id="previewSection">
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="justify-content-start">
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        {/* {typeof onClose === 'function' && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            style={{ height: 40, marginLeft: 16 }}
            onClick={onClose}
          >
            Hide Preview
          </Button>
        )} */}
        <div className="justify-content-end text-end">
          <p className="mb-0">
            <b>File Name</b>: {selectedFile?.fileName || "-"}
          </p>
        </div>
      </div>

      <div className="table-responsive border rounded">
        <table className="table table-bordered table-striped mb-0">
          <thead className="table-dark">
            <tr>
              {allData.length > 0 &&
                getTableHeaders().map((header, index) => {
                  const normalizedKey = header
                    .toUpperCase()
                    .replace(/\s+/g, "_");
                  const sortableFields = getSortableFields();
                  const isSortable = sortableFields.includes(normalizedKey);

                  return (
                    <th
                      className="sortable-header"
                      key={index}
                      scope="col"
                      onClick={() => isSortable && handleSort(normalizedKey)}
                      style={{
                        cursor: isSortable ? "pointer" : "default",
                        userSelect: "none",
                        position: "relative",
                      }}
                    >
                      {header.replace(/_/g, " ")}
                      {isSortable && renderSortIcon(normalizedKey)}
                    </th>
                  );
                })}
            </tr>
          </thead>

          <tbody>
            {allData.length === 0 ? (
              <tr>
                <td colSpan={getTableHeaders().length} className="text-center">
                  No data available
                </td>
              </tr>
            ) : getFilteredTableData().length === 0 ? (
              <tr>
                <td colSpan={getTableHeaders().length} className="text-center">
                  No records found
                </td>
              </tr>
            ) : (
              getFilteredTableData().map((row, index) => (
                <tr key={index}>
                  <td>
                    {searchTerm.trim().length > 0
                      ? index + 1
                      : (currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  {getTableHeaders()
                    .slice(1)
                    .map((header, idx) => (
                      <td key={idx}>
                        {/* {header === "LOAD_DATE"
                          ? row[header.toLowerCase()] || row[header]
                            ? require("dayjs")(
                                row[header.toLowerCase()] || row[header]
                              ).format("DD-MM-YYYY")
                            : "-"
                          : row[header.toLowerCase()] || row[header] || "-"} */}
                           {(() => {
                              const lowerKey = header.toLowerCase();
                              const value = row[lowerKey] || row[header];

                              switch (header) {
                                case "LOAD_DATE":
                                  return value
                                    ? require("dayjs")(value).format("DD-MM-YYYY")
                                    : "-";
                                case "TYPE":
                                  return row["type"] || row["FILE_TYPE"] || row["file_type"] || "";
                                default:
                                  return value || "-";
                              }
                            })()}
                      </td>
                    ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {!allData.length === 0 && (
        <div className="d-flex justify-content-end align-items-center mt-3">
          <div className="pagination">
            <Button
              variant="outlined"
              size="small"
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className="me-2"
            >
              First
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="me-2"
            >
              Prev
            </Button>
            <span className="mx-2 align-self-center">
              Page {currentPage} of{" "}
              {Math.max(1, Math.ceil(totalItems / itemsPerPage))}
            </span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleNextPage}
              disabled={
                currentPage === Math.ceil(totalItems / itemsPerPage) ||
                totalItems === 0
              }
              className="me-2"
            >
              Next
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLastPage}
              disabled={
                currentPage === Math.ceil(totalItems / itemsPerPage) ||
                totalItems === 0
              }
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PreviewFileTable;
